import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../backlog/backlog.css';
import './sqlQueryConsole.css';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, type ExportData } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { AppDialog } from '../../components/AppDialog';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import {
  executeQuery, getSchema, listSaved, createSaved, deleteSaved,
  type ExecuteResult, type ResultRow, type SchemaTable, type SavedQuery, type ForeignKey,
} from './api';
import type { SqlEditorHandle } from './SqlEditorCM';

// CodeMirror-based SQL editor (schema-aware autocomplete, MSSQL highlighting).
// LAZY-loaded so CodeMirror is code-split into its own chunk and never lands in
// the main app bundle — it only downloads when this admin/service page is opened.
const SqlEditor = lazy(() => import('./SqlEditorCM'));

// Visual Query Builder — heavy tool, lazy-loaded so it only downloads when opened.
const QueryBuilderLazy = lazy(() => import('./QueryBuilder'));

/* ── client-side mirror of ReadOnlySqlGuard (instant feedback; the server is
      authoritative — see ReadOnlySqlGuard.cs) ── */
const BANNED = ['INSERT', 'UPDATE', 'DELETE', 'DROP', 'ALTER', 'TRUNCATE', 'CREATE', 'MERGE', 'EXEC', 'EXECUTE', 'GRANT', 'REVOKE', 'DENY', 'INTO', 'BACKUP', 'RESTORE', 'SHUTDOWN', 'RECONFIGURE', 'WAITFOR', 'SP_', 'XP_'];

function validateSql(raw: string): { ok: boolean; reason?: string } {
  const q = (raw || '').replace(/\/\*[\s\S]*?\*\//g, ' ').replace(/--[^\n]*/g, ' ').trim().replace(/;+\s*$/, '');
  if (!q) return { ok: false, reason: 'Empty query' };
  if (q.includes(';')) return { ok: false, reason: 'Only a single statement is allowed' };
  if (!/^\s*(SELECT|WITH)\b/i.test(q)) return { ok: false, reason: 'Query must begin with SELECT' };
  const up = ' ' + q.toUpperCase() + ' ';
  for (const b of BANNED) {
    const re = b.endsWith('_') ? new RegExp('\\b' + b) : new RegExp('\\b' + b + '\\b');
    if (re.test(up)) return { ok: false, reason: `${b.replace('_', '')} is not permitted — read-only` };
  }
  return { ok: true };
}

const DEFAULT_SQL = 'SELECT TOP 100 *\nFROM INFORMATION_SCHEMA.TABLES\nORDER BY TABLE_SCHEMA, TABLE_NAME;';

type Msg = { kind: 'info' | 'ok' | 'err' | 'blk'; text: string };

export default function SqlQueryConsolePage() {
  const notify = useNotify();
  const confirm = useConfirm();

  const [sql, setSql] = useState(DEFAULT_SQL);
  const [result, setResult] = useState<ExecuteResult | null>(null);
  const [messages, setMessages] = useState<Msg>({ kind: 'info', text: 'Ready. Execution messages appear here.' });
  const [resTab, setResTab] = useState<'results' | 'messages'>('results');
  const [busy, setBusy] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);

  const [schema, setSchema] = useState<SchemaTable[]>([]);
  const [fks, setFks] = useState<ForeignKey[]>([]);
  const [schemaBusy, setSchemaBusy] = useState(false);
  const [schemaSearch, setSchemaSearch] = useState('');
  const [openTables, setOpenTables] = useState<Record<string, boolean>>({});

  const [railTab, setRailTab] = useState<'schema' | 'saved'>('schema');
  const [saved, setSaved] = useState<SavedQuery[]>([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: '', description: '', category: '' });
  const [saving, setSaving] = useState(false);
  const [qbOpen, setQbOpen] = useState(false);

  const editorRef = useRef<SqlEditorHandle | null>(null);
  const guard = useMemo(() => validateSql(sql), [sql]);

  /* ── schema shaped for CodeMirror's SQL autocomplete: { Table: [cols],
        dbo.Table: [cols] } ── */
  const cmSchema = useMemo(() => {
    const o: Record<string, string[]> = {};
    for (const t of schema) {
      const cols = t.columns.map(c => c.name);
      o[t.table] = cols;
      o[`${t.schema}.${t.table}`] = cols;
    }
    return o;
  }, [schema]);

  /* ── schema loads once (single, fixed data source) ── */
  useEffect(() => {
    let alive = true;
    (async () => {
      setSchemaBusy(true);
      const t0 = performance.now();
      try {
        const s = await getSchema();
        if (!alive) return;
        setSchema(s.tables);
        setFks(s.foreignKeys);
        setLoadMs(Math.round(performance.now() - t0));
      } catch (e) {
        if (alive) notify.error(`Schema unavailable: ${(e as Error).message}`);
      } finally {
        if (alive) setSchemaBusy(false);
      }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── insert a table/column name at the caret (schema double-click) ── */
  const insertAtCursor = useCallback((txt: string) => {
    const ed = editorRef.current;
    if (ed) ed.insertText(txt);
    else setSql(s => s + txt);   // editor chunk still loading — append as a fallback
  }, []);

  const clearAll = () => {
    setSql('');
    setResult(null);
    setMessages({ kind: 'info', text: 'Ready. Execution messages appear here.' });
    setResTab('results');
    editorRef.current?.focus();
  };

  const runSql = useCallback(async (text: string) => {
    if (busy) return;
    const chk = validateSql(text);
    if (!chk.ok) {
      setResTab('messages');
      setMessages({ kind: 'blk', text: `${chk.reason}. Permitted: a single SELECT.` });
      notify.error(chk.reason ?? 'Query blocked');
      return;
    }
    setBusy(true);
    const t0 = performance.now();
    try {
      const r = await executeQuery(text);
      setLoadMs(Math.round(performance.now() - t0));
      if (r.succeeded) {
        setResult(r);
        setResTab('results');
        setMessages({ kind: 'ok', text: `✓ ${r.rowsReturned.toLocaleString()} row(s)${r.truncated ? ' (capped)' : ''} in ${(r.durationMs / 1000).toFixed(2)} s · ${r.dataSource}` });
        notify.success(`${r.rowsReturned.toLocaleString()} rows in ${(r.durationMs / 1000).toFixed(2)} s`);
      } else {
        setResult(null);
        setResTab('messages');
        setMessages({ kind: r.message && /read-only|permitted|single statement|begin with SELECT|Empty/i.test(r.message) ? 'blk' : 'err', text: r.message || 'Query failed.' });
        notify.error(r.message || 'Query failed');
      }
    } catch (e) {
      setResTab('messages');
      setMessages({ kind: 'err', text: (e as Error).message });
      notify.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [busy, notify]);

  const execute = useCallback(() => runSql(sql), [runSql, sql]);

  /* ── saved queries (shared, server-side store) ── */
  const refreshSaved = useCallback(async () => {
    try { setSaved(await listSaved()); } catch { /* surfaced by the axios interceptor */ }
  }, []);

  useEffect(() => { refreshSaved(); }, [refreshSaved]);

  const openSaveDialog = () => {
    if (!guard.ok) return;
    setSaveForm({ name: '', description: '', category: '' });
    setSaveOpen(true);
  };

  const submitSave = async () => {
    if (!saveForm.name.trim()) { notify.warning('A query name is required.'); return; }
    setSaving(true);
    try {
      await createSaved({
        name: saveForm.name.trim(),
        description: saveForm.description.trim() || null,
        category: saveForm.category.trim() || null,
        sqlText: sql,
      });
      setSaveOpen(false);
      notify.success('Saved successfully.');
      setRailTab('saved');
      await refreshSaved();
    } catch (e) {
      notify.error((e as Error).message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const loadSavedQuery = (q: SavedQuery) => {
    setSql(q.sqlText);
    setRailTab('schema');
    editorRef.current?.focus();
    notify.info(`Loaded "${q.name}" into the editor.`);
  };

  const removeSaved = async (q: SavedQuery) => {
    const ok = await confirm.confirm({ title: 'Delete saved query', message: `Delete "${q.name}"?`, tone: 'danger', confirmLabel: 'Delete' });
    if (!ok) return;
    try {
      await deleteSaved(q.oid);
      notify.success('Deleted successfully.');
      await refreshSaved();
    } catch (e) {
      notify.error((e as Error).message || 'Delete failed.');
    }
  };

  // Keys for the plain-textarea FALLBACK shown only while the CodeMirror chunk
  // loads (the CodeMirror editor binds F5 / Ctrl+Enter itself via its keymap).
  const onFallbackKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'F5' || (e.key === 'Enter' && (e.ctrlKey || e.metaKey))) {
      e.preventDefault();
      execute();
    }
  };

  /* ── results → shared grid (synthetic field ids so duplicate column names in a
        SELECT * across a join never collide) ── */
  const gridRows: ResultRow[] = useMemo(() => {
    if (!result) return [];
    return result.rows.map(arr => {
      const o: ResultRow = {};
      result.columns.forEach((_, i) => { o[`col_${i}`] = arr[i] ?? null; });
      return o;
    });
  }, [result]);

  const gridCols: AppColumn<ResultRow>[] = useMemo(() => {
    if (!result) return [];
    return result.columns.map((name, i) => ({
      field: `col_${i}`,
      header: name,
      headerText: name,
      text: (r: ResultRow) => r[`col_${i}`] ?? '',
      render: (r: ResultRow) => (r[`col_${i}`] === null ? <span className="qc-null">NULL</span> : r[`col_${i}`]),
    }));
  }, [result]);

  const exportData: ExportData | undefined = useMemo(() => {
    if (!result || !result.columns.length) return undefined;
    return {
      filename: `query-result-${Date.now()}`,
      headers: result.columns,
      rows: result.rows.map(r => r.map(c => c ?? '')),
    };
  }, [result]);

  const filteredSchema = useMemo(() => {
    const q = schemaSearch.trim().toLowerCase();
    if (!q) return schema;
    return schema
      .map(t => ({ ...t, columns: t.columns.filter(c => c.name.toLowerCase().includes(q)) }))
      .filter(t => t.table.toLowerCase().includes(q) || t.columns.length > 0);
  }, [schema, schemaSearch]);

  const schemaGroups = useMemo(() => {
    const g: Record<string, SchemaTable[]> = {};
    for (const t of filteredSchema) (g[t.schema] ||= []).push(t);
    return g;
  }, [filteredSchema]);

  // Keyboard shortcut (CLAUDE.md standing rule 14, §18.6, ADR-0013): Execute is
  // F5 / Ctrl+Enter via the CodeMirror keymap; the toolbar Clear button gets the
  // one shared hotkey here, mirroring its `busy` disabled state.  Alt+Shift+C = Clear.
  useHotkeys([
    { combo: 'alt+shift+c', label: 'Clear', group: 'SQL Query Console',
      run: () => clearAll(), disabled: busy },
  ]);

  return (
    <div className="bl-page qc-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" /><span>System</span>
            <i className="ti ti-chevron-right" /><span>SQL Query Console</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="sqlconsole" loadMs={loadMs} loading={busy || schemaBusy} auditFormName="SQL Query Console" />
          </div>
          <div className="dp-subtitle">
            Run controlled, read-only <b>SELECT</b> queries against the application database.
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <AppToolbar
          left={
            <>
              <button className="ca-btn" onClick={() => setQbOpen(true)} disabled={busy} title="Build a SELECT visually">
                <i className="ti ti-binary-tree" /> Query Builder
              </button>
              <button className="ca-btn" onClick={openSaveDialog} disabled={busy || !guard.ok} title="Save this query to the shared Query Library">
                <i className="ti ti-device-floppy" /> Save Query
              </button>
              <button className="ca-btn" onClick={clearAll} disabled={busy} title={`Clear (${formatCombo('alt+shift+c')})`}>
                <i className="ti ti-eraser" /> Clear
              </button>
            </>
          }
          onRefresh={execute}
          refreshing={busy}
          exportData={exportData}
          showMeta={false}
          right={
            <button className="ca-btn ca-btn-solid qc-exec-right" onClick={execute} disabled={busy || !guard.ok} title="Execute (F5)">
              <i className={'ti ' + (busy ? 'ti-loader-2 qc-spin' : 'ti-player-play')} /> {busy ? 'Running…' : 'Execute'} <span className="qc-kbd">F5</span>
            </button>
          }
        />

        <div className="qc-ws">
          {/* LEFT — editor + results */}
          <div className="qc-main">
            <div className="qc-panel">
              <div className="qc-phead">
                SQL Editor
                <span className="qc-pmeta" style={guard.ok ? undefined : { color: 'var(--qc-red)', fontWeight: 700 }}>
                  {guard.ok ? 'single SELECT' : guard.reason}
                </span>
              </div>
              <Suspense
                fallback={
                  <textarea
                    className="qc-editor"
                    value={sql}
                    spellCheck={false}
                    autoCapitalize="off"
                    autoCorrect="off"
                    placeholder="SELECT TOP 100 * FROM ..."
                    onChange={e => setSql(e.target.value)}
                    onKeyDown={onFallbackKeyDown}
                  />
                }
              >
                <SqlEditor ref={editorRef} value={sql} onChange={setSql} onExecute={execute} schema={cmSchema} />
              </Suspense>
            </div>

            <div className="qc-panel">
              <div className="qc-rtabs">
                <button className={'qc-rtab' + (resTab === 'results' ? ' active' : '')} onClick={() => setResTab('results')}>
                  Results <span className="cnt">{result ? result.rowsReturned.toLocaleString() : '—'}</span>
                </button>
                <button className={'qc-rtab' + (resTab === 'messages' ? ' active' : '')} onClick={() => setResTab('messages')}>
                  Messages
                </button>
              </div>

              {resTab === 'results' ? (
                result && result.columns.length ? (
                  <AppDataGrid<ResultRow> rows={gridRows} columns={gridCols} defaultPageSize={50} />
                ) : (
                  <div className="qc-empty"><b>No results yet</b><span>Press <b>Execute</b> (F5) to run the query.</span></div>
                )
              ) : (
                <div className="qc-msg">
                  {messages.kind === 'blk' ? (
                    <div className="qc-blk"><b>Query blocked — read-only policy.</b><br />{messages.text}</div>
                  ) : (
                    <span className={'qc-m-' + messages.kind}>{messages.text}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — schema browser + saved queries */}
          <div className="qc-rail">
            <div className="qc-panel">
              <div className="qc-rtabs">
                <button className={'qc-rtab' + (railTab === 'schema' ? ' active' : '')} onClick={() => setRailTab('schema')}>
                  Schema <span className="cnt">{schema.length}</span>
                </button>
                <button className={'qc-rtab' + (railTab === 'saved' ? ' active' : '')} onClick={() => setRailTab('saved')}>
                  Query Library <span className="cnt">{saved.length}</span>
                </button>
              </div>

              {railTab === 'saved' ? (
                <div className="qc-saved">
                  {saved.length === 0 ? (
                    <div className="qc-grp">Your Query Library is empty. Write a SELECT and click <b>Save Query</b>.</div>
                  ) : saved.map(q => (
                    <div className="qc-sq" key={q.oid}>
                      <div className="qc-sq-top">
                        <span className="qc-sq-name" title={q.name}>{q.name}</span>
                        {q.category && <span className="qc-sq-cat">{q.category}</span>}
                      </div>
                      {q.description && <div className="qc-sq-desc">{q.description}</div>}
                      <div className="qc-sq-acts">
                        <button className="qc-sq-btn" onClick={() => loadSavedQuery(q)}>Load</button>
                        <button className="qc-sq-btn" onClick={() => { setSql(q.sqlText); setRailTab('schema'); runSql(q.sqlText); }} disabled={busy}>Run</button>
                        <button className="qc-sq-btn danger" onClick={() => removeSaved(q)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <>
              <div className="qc-sb-search">
                <i className="ti ti-search" />
                <input placeholder="Search tables & columns…" value={schemaSearch} onChange={e => setSchemaSearch(e.target.value)} />
              </div>
              <div className="qc-tree">
                {schemaBusy ? (
                  <div className="qc-grp">Loading schema…</div>
                ) : Object.keys(schemaGroups).length === 0 ? (
                  <div className="qc-grp">No tables found</div>
                ) : (
                  Object.entries(schemaGroups).map(([schemaName, tables]) => (
                    <div key={schemaName}>
                      <div className="qc-grp">{schemaName.toUpperCase()}</div>
                      {tables.map(t => {
                        const key = `${t.schema}.${t.table}`;
                        const open = !!openTables[key] || !!schemaSearch;
                        return (
                          <div key={key}>
                            <div
                              className={'qc-tbl' + (open ? ' open' : '')}
                              onClick={() => setOpenTables(o => ({ ...o, [key]: !o[key] }))}
                              onDoubleClick={() => insertAtCursor(t.table)}
                              title="Click to expand · double-click to insert"
                            >
                              <span className="qc-tw">▶</span><span className="qc-tn">{t.table}</span><span className="cc">{t.columns.length}</span>
                            </div>
                            {open && (
                              <div className="qc-cols">
                                {t.columns.map(c => (
                                  <div
                                    key={c.name}
                                    className={'qc-col' + (c.isPrimaryKey ? ' pk' : '')}
                                    onDoubleClick={e => { e.stopPropagation(); insertAtCursor(c.name); }}
                                    title="Double-click to insert"
                                  >
                                    <span className="cn">{c.isPrimaryKey ? '🔑 ' : ''}{c.name}</span>
                                    <span className="ct">{c.dataType}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      </div>

      <AppDialog
        open={saveOpen}
        title="Save Query"
        icon="ti-device-floppy"
        width={520}
        onClose={() => setSaveOpen(false)}
        footer={
          <>
            <button className="ca-btn" onClick={() => setSaveOpen(false)} disabled={saving}>Cancel</button>
            <button className="ca-btn ca-btn-solid" onClick={submitSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </>
        }
      >
        <div className="qc-save-form">
          <label className="qc-ff">
            <span className="qc-ff-lbl">Query Name <b className="qc-req">*</b></span>
            <input className="qc-ff-in" autoFocus value={saveForm.name}
              onChange={e => setSaveForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Samples pending review" />
          </label>
          <label className="qc-ff">
            <span className="qc-ff-lbl">Category</span>
            <input className="qc-ff-in" value={saveForm.category}
              onChange={e => setSaveForm(f => ({ ...f, category: e.target.value }))}
              placeholder="e.g. Data Review" />
          </label>
          <label className="qc-ff">
            <span className="qc-ff-lbl">Description</span>
            <textarea className="qc-ff-in" style={{ minHeight: 54, resize: 'vertical' }} value={saveForm.description}
              onChange={e => setSaveForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What question does this answer?" />
          </label>
          <label className="qc-ff">
            <span className="qc-ff-lbl">SQL (read-only preview)</span>
            <pre className="qc-save-sql">{sql}</pre>
          </label>
        </div>
      </AppDialog>

      {qbOpen && (
        <Suspense fallback={null}>
          <QueryBuilderLazy
            schema={schema}
            foreignKeys={fks}
            onApply={(s) => { setSql(s); setQbOpen(false); requestAnimationFrame(() => editorRef.current?.focus()); }}
            onClose={() => setQbOpen(false)}
          />
        </Suspense>
      )}
    </div>
  );
}
